// frontend/src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import axiosInstance from '../services/axiosConfig';
import { useNavigate, useLocation } from 'react-router-dom';

const AuthContext = createContext(null);

export const useAuth = () => {
    return useContext(AuthContext);
};

// This allows our Axios interceptor to trigger a logout globally
let globalLogoutHandler = () => {};
export const triggerLogout = (message) => {
    if (globalLogoutHandler) {
        globalLogoutHandler(message);
    }
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const initialCheckDone = useRef(false);

    const checkAuthStatus = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/auth/status');
            if (response.data && response.data.authenticated) {
                setCurrentUser(response.data.user);
                return true;
            }
        } catch (err) {
            if (err.response?.status !== 401) {
                console.error("Auth check failed with unexpected status:", err);
            }
        }
        setCurrentUser(null);
        return false;
    }, []);

    const logout = useCallback(async (message) => {
        const wasLoggedIn = !!currentUser;
        setCurrentUser(null);
        setError(null);
        try {
            await axiosInstance.post('/auth/logout');
        } catch (err) {
            console.error('Backend logout failed:', err.response?.data || err.message);
        } finally {
            if (wasLoggedIn) {
                navigate('/login', { state: { message: message || "You have been logged out." }, replace: true });
            }
        }
    }, [navigate, currentUser]);

    useEffect(() => {
        globalLogoutHandler = logout;
        return () => {
            globalLogoutHandler = null;
        };
    }, [logout]);

    // This useEffect runs ONLY ONCE on initial application load
    useEffect(() => {
        if (!initialCheckDone.current) {
            initialCheckDone.current = true;
            setLoading(true);
            
            // --- MODIFICATION: Removed the unnecessary primeCsrfToken call ---
            // The initial GET /status call will now correctly deliver the first CSRF token.
            checkAuthStatus().finally(() => {
                setLoading(false);
            });
        }
    }, [checkAuthStatus]);

    const login = async (credentials) => {
        setLoading(true);
        setError(null);
        try {
            // The first CSRF token was retrieved by the checkAuthStatus() call on app load.
            await axiosInstance.post('/auth/login', credentials);
            const sessionVerified = await checkAuthStatus();

            if (sessionVerified) {
                console.log("Session verified successfully after login.");
                const from = location.state?.from?.pathname || '/account/reservations';
                navigate(from, { replace: true });
                return true;
            } else {
                throw new Error("Login succeeded, but the session could not be established. Please clear your browser cookies and try again.");
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Login failed.';
            console.error("AuthContext: Login FAILED.", errorMessage);
            setError(errorMessage);
            setCurrentUser(null);
            // setLoading(false); // This line can be removed as 'finally' will handle it.
            throw err;
        } finally {
            // This block will run after the try or catch block completes.
            setLoading(false);
        }
    };

    const register = async (credentials) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.post('/auth/register', credentials);
            setLoading(false);
            return response.data;
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Registration error occurred.';
            setError(errorMessage);
            console.error("Registration error in AuthContext:", err.response?.data || err.message);
            setLoading(false);
            throw err;
        }
    };

    const value = {
        currentUser,
        loading,
        error,
        isAuthenticated: !!currentUser,
        login,
        logout,
        register,
        setError
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};